/**
 * Integrytas Group - Common Security Module (CSM)
 * Self-service registration. New users land in csm_user_master with is_active='N'
 * (per the auto-provisioning trigger) until an admin promotes them.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { APPLICATION_NAME, BRANDING_SUFFIX } from '../../constants/branding';
import type { SignUpInput } from '../../lib/use_auth_session';
import { Watermark } from '../common/Watermark';

export interface RegisterScreenProps {
  onRegister: (input: SignUpInput) => Promise<{ needsEmailConfirmation: boolean }>;
  onBackToSignIn: () => void;
  errorMessage?: string | null;
}

export function RegisterScreen({ onRegister, onBackToSignIn, errorMessage }: RegisterScreenProps) {
  const { t } = useTranslation();
  const [email, setEmail]             = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword]       = useState('');
  const [confirm, setConfirm]         = useState('');
  const [busy, setBusy]               = useState(false);
  const [done, setDone]               = useState<null | { needsEmailConfirmation: boolean }>(null);
  const [localError, setLocalError]   = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    if (password.length < 8) {
      setLocalError(t('auth.register.password_min_error'));
      return;
    }
    if (password !== confirm) {
      setLocalError(t('auth.passwords_dont_match'));
      return;
    }
    setBusy(true);
    try {
      const result = await onRegister({
        email, password, display_name: displayName || undefined,
      });
      setDone(result);
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="relative flex h-screen w-screen items-center justify-center bg-csm-surface-alt">
        <Watermark appName={APPLICATION_NAME} brandingSuffix={BRANDING_SUFFIX} />
        <div className="w-full max-w-sm rounded border border-csm-border bg-white p-6 shadow-sm">
          <h1 className="mb-2 text-csm-lg font-semibold text-csm-ink">{t('auth.register.done_title')}</h1>
          <p className="mb-4 text-csm-sm text-csm-ink-soft">
            {done.needsEmailConfirmation
              ? t('auth.register.done_needs_confirmation')
              : t('auth.register.done_active')}
          </p>
          <button
            type="button"
            onClick={onBackToSignIn}
            className="h-8 w-full rounded border border-primary bg-primary text-csm-md text-white hover:bg-primary-hover"
          >
            {t('auth.register.back_to_sign_in')}
          </button>
        </div>
      </div>
    );
  }

  const errMsg = localError || errorMessage;

  return (
    <div className="relative flex h-screen w-screen items-center justify-center bg-csm-surface-alt">
      <div
        aria-hidden
        className="pointer-events-none absolute right-3 top-1 select-none text-csm-xs tracking-wide text-csm-border-strong"
      >
        {APPLICATION_NAME} <span className="font-medium">{BRANDING_SUFFIX}</span>
      </div>

      <form
        onSubmit={submit}
        className="w-full max-w-sm rounded border border-csm-border bg-white p-6 shadow-sm"
      >
        <h1 className="mb-1 text-csm-lg font-semibold text-csm-ink">{t('auth.register.title')}</h1>
        <p className="mb-4 text-csm-sm text-csm-muted">
          {t('auth.register.subtitle')}
        </p>

        <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('auth.register.email_label')}</label>
        <input
          type="email" required autoComplete="email"
          value={email} onChange={e => setEmail(e.target.value)}
          className="mb-3 h-8 w-full rounded border border-csm-border px-2 text-csm-md focus:border-csm-watermark focus:outline-none"
        />

        <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('auth.register.display_name_label')}</label>
        <input
          type="text" autoComplete="name"
          value={displayName} onChange={e => setDisplayName(e.target.value)}
          className="mb-3 h-8 w-full rounded border border-csm-border px-2 text-csm-md focus:border-csm-watermark focus:outline-none"
        />

        <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('auth.register.password_label')}</label>
        <input
          type="password" required autoComplete="new-password" minLength={8}
          value={password} onChange={e => setPassword(e.target.value)}
          className="mb-3 h-8 w-full rounded border border-csm-border px-2 text-csm-md focus:border-csm-watermark focus:outline-none"
        />

        <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('auth.register.confirm_password_label')}</label>
        <input
          type="password" required autoComplete="new-password" minLength={8}
          value={confirm} onChange={e => setConfirm(e.target.value)}
          className="mb-3 h-8 w-full rounded border border-csm-border px-2 text-csm-md focus:border-csm-watermark focus:outline-none"
        />

        {errMsg && (
          <div className="mb-3 rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-1 text-csm-sm text-csm-danger">
            {errMsg}
          </div>
        )}

        <button
          type="submit" disabled={busy}
          className="mb-3 h-8 w-full rounded border border-primary bg-primary text-csm-md text-white hover:bg-primary-hover disabled:opacity-60"
        >
          {busy ? t('auth.register.submitting') : t('auth.register.submit')}
        </button>

        <button
          type="button" onClick={onBackToSignIn}
          className="h-8 w-full rounded border border-csm-border bg-white text-csm-sm text-csm-ink-soft hover:bg-csm-surface-alt"
        >
          {t('auth.register.back_to_sign_in')}
        </button>
      </form>
    </div>
  );
}
